/**
 * Quest UI Configuration
 *
 * Data-driven knobs for the quest-facing UI: the always-on docked tracker
 * (top-right HUD objective list) and the shared accent palette consumed by both
 * the tracker and the Quest Log window. The structural config (anchors, colors,
 * status accents, type icons) stays a pure leaf — components pull theme tokens
 * themselves and merge these in.
 *
 * The `formatQuestRewards` normalizer + `<QuestRewards>` presentational helper
 * below are the ONE place quest rewards render, so the two call sites
 * (NPC offer card with flat `rewardXP/rewardGold/rewardItems`, and the Quest Log
 * with `rewards.{xp,gold,items}`) stay consistent — same glyphs, same layout.
 */

import React from 'react';
import type { QuestType } from '../../shared/types/Quest';
import { SPACE, FONT_SIZE } from '../../game/ui/theme';
import { formatGold } from '../../game/ui/format';

export const QUEST_UI_CONFIG = {
  /** Always-visible objective tracker docked at the top-right of the HUD,
   *  tucked BELOW the corner minimap (minimap is top:10 / 180×140 → bottom ~150;
   *  this clears it with a small gap and aligns to the same right edge). */
  tracker: {
    /** Screen anchor (px from the top-right corner). */
    anchor: { top: 200, right: 10 },
    /** Panel width. */
    width: 264,
    /** Max number of active quests shown at once (overflow → "+N more"). */
    maxTracked: 5,
    /** Stacking order — matches the other docked HUD frames. */
    zIndex: 100,
    /** Idle opacity; the tracker brightens to 1 on hover. */
    idleOpacity: 0.82,
  },

  /** Accent colours shared across quest surfaces (not in the semantic token set). */
  colors: {
    /** Completed-objective green. */
    complete: '#00ff88',
    /** In-progress objective bar. */
    progress: '#4a9eff',
    /** Objective count text. */
    objective: '#cfd8e3',
    /** Quest description text. */
    description: '#b8c2cc',
  },

  /** Per-status accent (icon + left border) for the NPC offer/turn-in cards. */
  status: {
    completed: { color: '#00ff88', icon: '🏆', label: 'Completed' },
    ready: { color: '#00ff88', icon: '📬', label: 'Ready to Turn In' },
    active: { color: '#ffd700', icon: '📝', label: 'In Progress' },
    available: { color: '#4a9eff', icon: '📜', label: 'Available' },
  },
} as const;

/** Resolve the accent bundle for a quest's current state. */
export function questStatusAccent(isCompleted: boolean, isAccepted: boolean, isTurnInReady = false) {
  if (isCompleted) return QUEST_UI_CONFIG.status.completed;
  if (isTurnInReady) return QUEST_UI_CONFIG.status.ready;
  if (isAccepted) return QUEST_UI_CONFIG.status.active;
  return QUEST_UI_CONFIG.status.available;
}

/** Emoji glyph per objective type — surfaced next to the tracker title. */
export const QUEST_TYPE_ICON: Record<QuestType, string> = {
  kill: '⚔️',
  collect: '🎒',
  explore: '🧭',
  level: '⭐',
  talk: '💬',
  equip: '🛡️',
};

/**
 * Human-readable objective progress, e.g. "3 / 10". An equip objective is
 * worn or not (plan 24 D31), so it reads "Equipped" / "Not equipped".
 */
export function formatObjectiveProgress(currentCount: number, targetCount: number, type?: QuestType): string {
  if (type === 'equip') return currentCount >= Math.max(targetCount, 1) ? 'Equipped' : 'Not equipped';
  return `${currentCount} / ${Math.max(targetCount, 1)}`;
}

/** Derive a readable objective label from the quest type + target. */
export function formatObjectiveLabel(
  type: QuestType,
  targetId: string,
  targetCount: number,
  fallbackIndex = 0,
): string {
  const target = targetId || 'target';
  const count = Math.max(targetCount, 1);
  switch (type) {
    case 'kill': return `Defeat ${count} ${target}`;
    case 'collect': return `Collect ${count} ${target}`;
    case 'explore': return count === 1 ? `Visit ${target}` : `Visit ${target} ${count} times`;
    case 'talk': return `Talk to ${target}`;
    case 'level': return `Reach Level ${count}`;
    case 'equip': return `Equip ${target}`;
    default: return `Objective ${fallbackIndex + 1}`;
  }
}

/** Compact target label kept separate from authored quest directions. */
export function formatObjectiveTargetLabel(type: QuestType, targetName: string, targetCount: number): string {
  const count = Math.max(targetCount, 1);
  if (type === 'level') return targetName;
  if (type === 'talk' || type === 'explore' || type === 'equip') return targetName;
  return `${targetName} ×${count}`;
}

// ── Quest rewards (single render path) ─────────────────────────────────────
//
// The reward row used to be duplicated between the NPC offer card and the
// Quest Log, with divergent XP glyphs (⭐ vs ✨) and incompatible data shapes.
// `formatQuestRewards` normalizes either shape to `{ xp, gold, items }`, and
// `<QuestRewards>` renders the canonical row. Glyphs: ⭐ XP · 💰 gold · 🎁 item.

/** Normalized reward shape consumed by `<QuestRewards>`. */
export interface NormalizedQuestRewards {
  xp: number;
  gold: number;
  /** Display labels for any reward items. */
  items: string[];
}

/** The two reward shapes seen in the codebase (flat NPCData vs nested log). */
interface FlatQuestRewards {
  rewardXP?: number;
  rewardGold?: number;
  rewardItems?: string[];
}
interface NestedQuestRewards {
  xp?: number;
  gold?: number;
  items?: Array<{ id: string; quantity?: number; name?: string }>;
}

/** Normalize either reward shape (flat NPCData.Quest or nested QuestRewards). */
export function formatQuestRewards(
  source: FlatQuestRewards | NestedQuestRewards,
): NormalizedQuestRewards {
  const flat = source as FlatQuestRewards;
  const nested = source as NestedQuestRewards;

  const xp = flat.rewardXP ?? nested.xp ?? 0;
  const gold = flat.rewardGold ?? nested.gold ?? 0;

  let items: string[] = [];
  if (Array.isArray(flat.rewardItems)) {
    items = flat.rewardItems;
  } else if (Array.isArray(nested.items)) {
    items = nested.items.map(it => {
      const label = it.name ?? it.id;
      return it.quantity && it.quantity > 1 ? `${label} ×${it.quantity}` : label;
    });
  }

  return { xp, gold, items };
}

/** Glyphs for the reward row — XP standardized on ⭐ (was ⭐ here, ✨ in the log). */
const REWARD_GLYPH = { xp: '⭐', item: '🎁' } as const;

/**
 * `<QuestRewards>` — the canonical reward-row body. Layout-only (a flex row of
 * spans); the caller owns the surrounding container so it can keep its own
 * border/padding/colour. Built with `createElement` so this stays a `.ts` leaf.
 */
export const QuestRewards: React.FC<{
  rewards: FlatQuestRewards | NestedQuestRewards;
}> = ({ rewards }) => {
  const { xp, gold, items } = formatQuestRewards(rewards);
  const spanStyle: React.CSSProperties = { whiteSpace: 'nowrap' };
  return React.createElement(
    'div',
    {
      style: {
        display: 'flex',
        gap: SPACE.lg,
        flexWrap: 'wrap',
        fontSize: FONT_SIZE.sm,
      },
    },
    React.createElement('span', { key: 'xp', style: spanStyle }, `${REWARD_GLYPH.xp} ${xp} XP`),
    React.createElement('span', { key: 'gold', style: spanStyle }, `${formatGold(gold)} Gold`),
    ...items.map((item, i) =>
      React.createElement('span', { key: `item-${i}`, style: spanStyle }, `${REWARD_GLYPH.item} ${item}`),
    ),
  );
};
